import type { ComponentProps, ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

/**
 *	Figma calls the ordinary variant `active`. That word already means "the page
 *	you are on" in this workspace, so it is `default` here and the Code Connect
 *	mapping translates at the boundary.
 */
export type MenuItemVariant = 'default' | 'selected' | 'destructive';

const DefaultMenuItemVariant: MenuItemVariant = 'default' as const;

export type MenuItemSize = Extract<Size, 'sm' | 'md'>;

const DefaultMenuItemSize: MenuItemSize = 'md' as const;

/**
 *	A row is a button, which is what a menu of actions wants. Inside a listbox
 *	it is an option instead — the one other role a `<button>` may carry that
 *	fits a row you pick from — and the parent that claims `listbox` is the one
 *	that asks for it. See `compositions/mention-list`.
 */
export type MenuItemRole = 'option';

type MenuItemEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface MenuItemDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface MenuItemAriaAttributes {
	[key: `aria-${string}`]: string | number | boolean | undefined;
}

interface MenuItemProperties
	extends
		MenuItemEventHandlers,
		MenuItemDataAttributes,
		MenuItemAriaAttributes {
	variant?: MenuItemVariant;
	size?: MenuItemSize;
	disabled?: boolean;
	/**
	 *	Paints the hover surface without a pointer on the row. For a list the
	 *	keyboard moves through while focus stays in a field, where the row
	 *	under the arrow keys has to look the way the one under the pointer does.
	 */
	highlighted?: boolean;
	role?: MenuItemRole;
	/**
	 *	Accepted for the same reason the checkbox accepts one: an option is
	 *	pointed at by the field's `aria-activedescendant`, and that needs an id
	 *	to point at. It is not a hook for styling.
	 */
	id?: string;

	label: string;
	/** A second line under the label, in the quieter ink. The row grows to fit. */
	description?: string;

	leadingSlot?: ReactNode;
	trailingSlot?: ReactNode;
}

export function MenuItem({
	variant = DefaultMenuItemVariant,
	size = DefaultMenuItemSize,
	disabled = false,
	highlighted = false,
	role,
	id,

	label,
	description,

	leadingSlot,
	trailingSlot,

	...props
}: MenuItemProperties): ReactNode {
	/**
	 *	`size` is not indexed the way `variant` is: both unions have a `default`
	 *	member, so `css[size]` would reach the *variant* class of that name and
	 *	quietly apply it. Only `small` re-declares anything, as on the button.
	 */
	const className: string = cn(
		css.menuItem,
		css[variant],
		size === 'sm' && css.sm,
		highlighted && css.highlighted,
		description !== undefined && css.detailed
	);

	return (
		<button
			{...props}
			type="button"
			role={role}
			id={id}
			/**
			 *	An option is reached through the field's `aria-activedescendant`,
			 *	never by Tab: focus stays in the field, and a Tab that landed on a
			 *	row would blur the field and close the list under its own button.
			 */
			tabIndex={role === 'option' ? -1 : undefined}
			aria-current={variant === 'selected' ? true : undefined}
			data-variant={variant}
			data-size={size}
			data-highlighted={highlighted || undefined}
			className={className}
			disabled={disabled}>
			{leadingSlot}
			<span className={css.text}>
				<span className={css.label}>{label}</span>
				{description !== undefined && (
					<span className={css.description}>{description}</span>
				)}
			</span>
			{trailingSlot}
		</button>
	);
}
